import { Component, Input, OnInit } from '@angular/core';
import { ColDef, GridApi, GridOptions, GridReadyEvent, IDatasource, IGetRowsParams } from 'ag-grid-community';
import { Case, CaseResponse } from '../interfaces/mug-case.interface';
import { ReducerUtilityService } from '../../../../shared/utility-services/reducer-utility.service';
import { BaseAgency, ENTITLEMENTS, EntitlementsHelperService, LINE_OF_BUSINESS_ENUM, LOGIN_INFO_REDUCER_NAME, USER_ROLE, UserInfoStateStructure } from '@ptl/account-management';
import { PtlWindowLocationService } from '@ptl/global-components';
import { MassUploadGridCaseService } from '../services/mug-case.service';
import { CASE_STATUS } from '../interfaces/mug-submission.interface';
import { DATA_DISPLAY_CARD_STYLE } from 'src/app/angular-elements/shared/components/data-display-card/interfaces/data-display-card.interface';
import { AgActionMenuCellRendererComponent } from 'src/app/angular-elements/shared/components/ag-grid-cell-renderers/ag-action-menu-cell-renderer/ag-action-menu-cell-renderer.component';
import { ActionItem, ActionItemRendererParams } from 'src/app/angular-elements/shared/components/ag-grid-cell-renderers/interfaces/ag-grid-cell-renderer.interface';
import { DateService } from '../../../../shared/services/date.service';
@Component({
  selector: 'app-mass-upload-grid-dashboard',
  templateUrl: './mass-upload-grid-dashboard.component.html',
  styleUrls: ['./mass-upload-grid-dashboard.component.scss'],
  standalone: false
})
export class MassUploadGridDashboardComponent implements OnInit {
  @Input() massUploadGridUrlThisYear: string = '/individual/tools/mug/enrollment-grid';
  @Input() massUploadGridUrlNextYear: string = '/individual/tools/mug/enrollment-grid-2027';
  @Input() massUploadSubmissionUrl: string = '/individual/tools/mug/submission-log';

  CREATED_BY_FIELD = 'createdBy';
  AGENCY_ID_FIELD = 'agencyId';
  ECS_COLUMN = 'ecsStatus';

  gridApi: GridApi | null = null;
  columnDefs: ColDef[] = [];

  // Pagination settings
  paginationPageSize = 10;
  cacheBlockSize = 10;

  // Grid options
  gridOptions: GridOptions = {
    pagination: true,
    paginationPageSize: this.paginationPageSize,
    paginationPageSizeSelector: [10, 20, 50],
    cacheBlockSize: this.cacheBlockSize,
    rowModelType: 'infinite',
    maxBlocksInCache: 10
  };

  hasEntitlement = false;
  isLoading = true;
  errorMessage: string = null;
  loginData: UserInfoStateStructure;
  agency: BaseAgency;

  // Track current page
  currentPage = 0;
  totalPages = 0;
  totalElements = 0;

  // Track sorting and filtering
  currentSortBy: string | null = null;
  currentSortDirection: 'ASC' | 'DESC' | null = null;
  currentFilters: { [key: string]: string } = {};

  // Case status
  numCasesInProgress: number = 0;
  numCasesSubmitted: number = 0;

  THEMES = {
    IN_PROGRESS: DATA_DISPLAY_CARD_STYLE.IN_PROGRESS,
    SUCCESS: DATA_DISPLAY_CARD_STYLE.SUCCESS,
    DEFAULT: DATA_DISPLAY_CARD_STYLE.DEFAULT
  };
  
  constructor(
    private reducerUtilityService: ReducerUtilityService,
    private entitlementsHelperService: EntitlementsHelperService,
    private windowLocationService: PtlWindowLocationService,
    private caseService: MassUploadGridCaseService,
    private dateService: DateService
  ) {}

  async ngOnInit() {
    this.loginData = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    const hasMassEnrollEntitlement = await this.entitlementsHelperService.entitlementValueForSelectedAgencyByNameAndLob(
      ENTITLEMENTS.MASS_UPLOAD,
      LINE_OF_BUSINESS_ENUM.INDIVIDUAL,
      this.loginData?.userInfo?.role as USER_ROLE
    );

    if (hasMassEnrollEntitlement) {
      this.hasEntitlement = true;
    }
    else {
      this.errorMessage = 'Your currently selected agency does not have access to view Mass Upload. Try a different agency if available.';
      return;
    }

    const agency = this.entitlementsHelperService.getSelectedAgency(LINE_OF_BUSINESS_ENUM.INDIVIDUAL);
    this.agency = agency;

    this.caseService.loadAllCaseStatus(this.agency.agencyId).then((response) => {
      const results = response?.results;
      if (results) {
        this.numCasesInProgress = results.find(c => c.status === CASE_STATUS.IN_PROGRESS)?.count ?? 0;
        this.numCasesSubmitted = results.find(c => c.status === CASE_STATUS.SUBMITTED)?.count ?? 0;
      }
    })
    .catch((error) => {
      console.error('Error loading case status:', error);
    });
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;

    const datasource: IDatasource = {
      getRows: (params: IGetRowsParams) => {
        this.loadCaseDataForPage(params);
      }
    };

    this.gridApi.setGridOption('datasource', datasource);
    this.generateColumnDefs();

    this.gridApi.addEventListener('sortChanged', () => this.onSortChanged());
    this.gridApi.addEventListener('filterChanged', () => this.onFilterChanged());
  }

  // ─── Menu item builders ────────────────────────────────────────────────────

  getMenuItems(rowData: Case): ActionItem[] {
    if (!rowData) return [];

    const actions: { [key: string]: ActionItem } = {
      edit:     { id: 'edit',     label: 'Edit',     callback: (r) => this.editCase(r) },
      delete:   { id: 'delete',   label: 'Delete',   callback: (r) => this.deleteCase(r) },
      view:     { id: 'view',     label: 'View',     callback: (r) => this.viewCase(r) },
      //download: { id: 'download', label: 'Download', callback: (r) => this.downloadCase(r) }
    };

    switch (rowData.caseStatus) {
      case CASE_STATUS.IN_PROGRESS: return [actions.edit, actions.delete];
      case CASE_STATUS.SUBMITTED: return [actions.view];
      default: return [actions.view];
    }
  }

  // ─── Case actions ──────────────────────────────────────────────────────────

  createNewCase() {
    this.windowLocationService.goToUrl(this.massUploadGridUrlThisYear);
  }

  showAllSubmissions() {
    this.windowLocationService.goToUrl(this.massUploadSubmissionUrl);
  }

  private async editCase(rowData: Case): Promise<void> {
    let currentYear = this.dateService.getTodayAsMoment().year().toString();
    let effectiveDate = '';
    this.isLoading = true;
    
    try {
      const enrollmentRequestResponse = await this.caseService.loadEnrollmentsForCaseId(rowData.caseId.toString(),  this.agency.agencyId);
      effectiveDate = enrollmentRequestResponse?.enrollments[0]?.effectiveDate;
    }
    catch(e) {}

    if (!effectiveDate || currentYear === effectiveDate.split('/')[2]) {
      this.windowLocationService.goToUrl(`${this.massUploadGridUrlThisYear}?caseId=${rowData.caseId}`);
    }
    else {
      this.windowLocationService.goToUrl(`${this.massUploadGridUrlNextYear}?caseId=${rowData.caseId}`);
    }

    this.isLoading = false;
  }

  private viewCase(rowData: Case): void {
    this.windowLocationService.goToUrl(`${this.massUploadSubmissionUrl}?caseId=${rowData.caseId}`);
  }

  private deleteCase(rowData: Case): void {
    this.caseService.removeCase(rowData.caseId?.toString(), this.loginData.userId, this.agency.agencyId);

    // clear the cache to purge the removed row from everywhere and refresh data 
    this.caseService.clearDashboardCache();
    this.resetPaginationAndRefresh();
  }

  // ─── Data loading ──────────────────────────────────────────────────────────

  private async loadCaseDataForPage(params: IGetRowsParams): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;

    try {
      const pageNumber = Math.floor(params.startRow / this.paginationPageSize);

      const queryParams: { [key: string]: any } = { page: pageNumber };

      if (this.currentSortBy) {
        queryParams['sortBy'] = this.currentSortBy;
        if (this.currentSortDirection) {
          queryParams['sortDirection'] = this.currentSortDirection;
        }
      }

      Object.keys(this.currentFilters).forEach(key => {
        queryParams[key] = this.currentFilters[key];
      });

      const response: CaseResponse = await this.caseService.fetchCaseData(this.agency.agencyId, queryParams);

      if (!response?.content) return;

      this.currentPage = response.number;
      this.totalPages = response.totalPages;
      this.totalElements = response.totalElements;

      params.successCallback(response.content, this.totalElements);

    } catch (error) {
      console.error('Error loading case data:', error);
      this.errorMessage = 'Failed to load case data. Please try again later.';
      params.failCallback();
    } finally {
      this.isLoading = false;
    }
  }

  // ─── Sort / filter ─────────────────────────────────────────────────────────

  private onSortChanged(): void {
    if (!this.gridApi) return;

    const columnState = this.gridApi.getColumnState();
    this.currentSortBy = null;
    this.currentSortDirection = null;

    if (columnState) {
      const sortedColumn = columnState.find(col => col.sort);
      if (sortedColumn) {
        this.currentSortBy = sortedColumn.colId;
        this.currentSortDirection = sortedColumn.sort === 'asc' ? 'ASC' : 'DESC';
      }
    }

    this.resetPaginationAndRefresh();
  }

  private onFilterChanged(): void {
    if (!this.gridApi) return;

    const filterModel = this.gridApi.getFilterModel();
    this.currentFilters = {};

    Object.keys(filterModel).forEach(key => {
      const filter = filterModel[key];
      if (filter.filter) {
        this.currentFilters[key] = filter.filter;
      }
    });

    this.resetPaginationAndRefresh();
  }

  private resetPaginationAndRefresh(): void {
    if (!this.gridApi) return;
    this.currentPage = 0;
    this.gridApi.purgeInfiniteCache();
  }

  // ─── Column definitions ────────────────────────────────────────────────────

  private formatHeaderName(fieldName: string): string {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  private generateColumnDefs(): void {
    let dataColumns = [];
    const columnOrder = [
      'caseId',
      this.CREATED_BY_FIELD,
      'creationDate',
      'caseType',
      'caseStatus',
      'submittedBy',
      'submissionDate',
      this.ECS_COLUMN,
      'lastUpdatedBy',
      'lastUpdatedDate',
      this.AGENCY_ID_FIELD,
      'agencyName',
      'agentName'
    ];
    const nonSearchableFields = this.getNonSearchableFields();

    const actionColumn: ColDef = {
      field: 'actions',
      headerName: 'Actions',
      cellRenderer: AgActionMenuCellRendererComponent,
      cellRendererParams: {
        getActionItems: (rowData: ActionItemRendererParams) => this.getMenuItems(rowData.data as unknown as Case)
      },
      sortable: false,
      resizable: false,
      filter: false,
      width: 80,
      pinned: 'left'
    };
    dataColumns.push(actionColumn);

    columnOrder.forEach((fieldName) => {
      let col: ColDef = {
        field: fieldName,
        headerName: this.formatHeaderName(fieldName),
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          filterOptions: ['contains'],
          showFunctionsInFilter: false,
          maxNumConditions: 1
        },
        sortable: true,
        resizable: true,
        flex: 1,
        minWidth: 200
      };

      if (nonSearchableFields.includes(fieldName)) {
        Object.assign(col, {sortable: false, filter: false});
      }

      dataColumns.push(col);
    });

    this.columnDefs = dataColumns;
  }

  private getNonSearchableFields() {
    let fields = [this.ECS_COLUMN];

    return fields;
  }
}
